import i18n from '../../i18n';
import { BaseApiClient } from './base-client';
import type { ApiResponse } from '../../types';

/**
 * Storage API - методы для работы с S3 хранилищем
 */
export class StorageApi extends BaseApiClient {
  
  // ===== STORAGE API =====
  
  /**
   * Удалить файлы из S3 хранилища
   * @param s3Keys - Массив S3 ключей для удаления
   */
  async deleteFromS3(s3Keys: string[]): Promise<ApiResponse<{
    total: number;
    deleted: number;
    failed: number;
    results: Array<{ s3Key: string; deleted: boolean; error: string | null }>;
  }>> {
    const response = await this.client.delete('/api/storage/delete', {
      data: { s3Keys }
    });
    return response.data;
  }

  /**
   * Копировать файл внутри S3 (без загрузки в браузер)
   * @param sourceS3Key - Исходный S3 ключ
   * @param destinationFolder - Папка назначения
   * @returns Объект с новым s3Key и publicUrl
   */
  async copyFileInS3(
    sourceS3Key: string,
    destinationFolder: string = 'user-uploads/video-frames-copies',
    sourceUrl?: string,
  ): Promise<ApiResponse<{
    s3Key: string;
    publicUrl: string;
  }>> {
    const response = await this.client.post('/api/storage/copy', {
      sourceS3Key,
      destinationFolder,
      ...(sourceUrl && { sourceUrl }),
    });
    return response.data;
  }

  /**
   * Download file via backend proxy (authenticated, returns blob)
   * Used for cross-origin downloads where direct fetch isn't possible
   */
  async proxyDownload(url: string, fileName: string): Promise<Blob> {
    const response = await this.client.get('/api/storage/proxy-download', {
      params: { url, filename: fileName },
      responseType: 'blob'
    });
    return response.data;
  }

  /**
   * Upload file via simple /api/upload endpoint (e.g. style images)
   * @param file - File to upload
   * @param type - Upload type hint for the server
   */
  async uploadFile(file: File, type: string = 'style-image'): Promise<{ success: boolean; url: string; message?: string }> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', type);
    const response = await this.client.post('/api/upload', formData);
    return response.data;
  }

  /**
   * Загрузить файл в S3 напрямую через presigned URL
   * @param file - Файл для загрузки
   * @param folder - Папка в S3 (по умолчанию 'user-uploads/images')
   * @param onProgress - Callback для отслеживания прогресса (0-100%)
   * @returns Объект с fileId, s3Key и publicUrl
   */
  async uploadFileToS3(
    file: File, 
    folder: string = 'user-uploads/images',
    onProgress?: (progress: number) => void
  ): Promise<{
    fileId: string;
    s3Key: string;
    publicUrl: string;
  }> {
    try {
      // 1. Получаем presigned URL для загрузки (теперь включает downloadUrl)
      const presignedResponse = await this.client.post('/api/upload/presigned-url', {
        fileName: file.name,
        contentType: file.type,
        size: file.size,
        folder
      });

      const { fileId, presignedUrl, s3Key, downloadUrl } = presignedResponse.data;

      // 2. Загружаем файл напрямую в S3 используя XMLHttpRequest для отслеживания прогресса
      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        
        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable && onProgress) {
            const progress = Math.round((e.loaded / e.total) * 100);
            onProgress(Math.max(1, progress)); // Минимум 1%
          }
        });

        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            onProgress?.(100);
            resolve();
          } else {
            reject(new Error(`Upload failed with status ${xhr.status}`));
          }
        });

        xhr.addEventListener('error', () => {
          reject(new Error('Upload failed'));
        });

        xhr.open('PUT', presignedUrl);
        xhr.setRequestHeader('Content-Type', file.type);
        xhr.send(file);
      });

      // 3. Используем downloadUrl, который пришёл в первом ответе
      // (долгосрочный presigned URL с 7 днями, создан на бэкенде)
      console.log('✅ File uploaded to S3:', {
        fileId,
        s3Key,
        fileName: file.name,
        size: `${(file.size / 1024 / 1024).toFixed(2)} MB`
      });

      return {
        fileId,
        s3Key,
        publicUrl: downloadUrl // Используем downloadUrl из первого ответа
      };
    } catch (error) {
      console.error('❌ Failed to upload file to S3:', error);
      throw new Error(i18n.t('common:error.uploadS3'));
    }
  }
}

